/**
 * Types and utilities for the Activity Panel feature.
 * Defines activity tracking interfaces, filter types, and icon/color mappings.
 */

export type ActivityType = 'page_visit' | 'action' | 'search' | 'document' | 'report';

export type ModuleFilterType = 'bids' | 'takeoffs' | 'proposals' | 'jobs' | 'customers' | 'vendors' | 'staff';

export type ActivityFilterType = 'all' | ActivityType | ModuleFilterType;

export interface ActivityItem {
  id: string;
  type: ActivityType;
  title: string;
  subtitle: string;
  timestamp: number;
  /**
   * Server-rendered date string, already in the tenant's configured date format
   * and timezone. Used as the display fallback when `timestamp` is unusable —
   * never re-parsed or re-formatted on the client.
   */
  timestampLabel?: string;
  pinned: boolean;
  deleted?: boolean;
  iconType: string;
  /** Fully-qualified backend model class, e.g. "App\\Models\\Job". Absent for local-only activities. */
  subjectType?: string;
  /** Primary key of the related record. Absent for local-only activities. */
  subjectId?: number;
}

export interface ActivityGroup {
  label: string;
  items: ActivityItem[];
}

/** Maps a route segment to a human-readable title and Material icon name. */
export interface RouteMapping {
  title: string;
  icon: string;
}

/** Route segment to title/icon lookup table. */
export const ROUTE_MAPPINGS: Record<string, RouteMapping> = {
  'dashboard': { title: 'Dashboard', icon: 'dashboard' },
  'customers': { title: 'Customers', icon: 'people' },
  'contacts': { title: 'Contacts', icon: 'contacts' },
  'jobs': { title: 'Jobs', icon: 'work' },
  'bids': { title: 'Bids', icon: 'gavel' },
  'proposals': { title: 'Proposals', icon: 'description' },
  'vendors': { title: 'Vendors', icon: 'store' },
  'warehouses': { title: 'Warehouse', icon: 'inventory_2' },
  'hr': { title: 'HR', icon: 'badge' },
  'settings': { title: 'Settings', icon: 'settings' },
  'tasks': { title: 'Tasks', icon: 'checklist' },
  'tickets': { title: 'Tickets', icon: 'confirmation_number' },
  'products': { title: 'Products', icon: 'category' },
  'services': { title: 'Services', icon: 'build' },
  'subcontractors': { title: 'Subcontractors', icon: 'engineering' },
  'submittals': { title: 'Submittals', icon: 'fact_check' },
  'notes': { title: 'Notes', icon: 'sticky_note_2' },
  'change-orders': { title: 'Change Orders', icon: 'swap_horiz' },
  'jobsites': { title: 'Jobsites', icon: 'location_on' },
  'document': { title: 'Documents', icon: 'folder' },
  'categories': { title: 'Categories', icon: 'category' },
};

/** Returns the icon color class for an activity type. */
export function getActivityIconColor(type: ActivityType): string {
  switch (type) {
    case 'page_visit': return '#3b82f6';
    case 'action': return '#10b981';
    case 'search': return '#8b5cf6';
    case 'document': return '#f97316';
    case 'report': return '#06b6d4';
    default: return '#6b7280';
  }
}

/** Returns the background color for an activity type icon badge. */
export function getActivityBgColor(type: ActivityType): string {
  switch (type) {
    case 'page_visit': return '#eff6ff';
    case 'action': return '#ecfdf5';
    case 'search': return '#f5f3ff';
    case 'document': return '#fff7ed';
    case 'report': return '#ecfeff';
    default: return '#f9fafb';
  }
}

/** Returns the border color for an activity type icon badge. */
export function getActivityBorderColor(type: ActivityType): string {
  switch (type) {
    case 'page_visit': return '#bfdbfe';
    case 'action': return '#bbf7d0';
    case 'search': return '#ddd6fe';
    case 'document': return '#fed7aa';
    case 'report': return '#a5f3fc';
    default: return '#e5e7eb';
  }
}

/** Returns the human-readable label for a type filter chip. */
export function getActivityTypeLabel(type: ActivityType): string {
  switch (type) {
    case 'page_visit': return 'Visited';
    case 'action': return 'Action';
    case 'search': return 'Search';
    case 'document': return 'Document';
    case 'report': return 'Report';
    default: return '';
  }
}

/** Filter chip definitions for the filter bar. */
export const FILTER_CHIPS: { label: string; value: ActivityFilterType }[] = [
  { label: 'All', value: 'all' },
  { label: 'Bids', value: 'bids' },
  { label: 'Takeoffs', value: 'takeoffs' },
  { label: 'Proposals', value: 'proposals' },
  { label: 'Jobs', value: 'jobs' },
  { label: 'Customers', value: 'customers' },
  { label: 'Vendors', value: 'vendors' },
  { label: 'Staff', value: 'staff' },
];
